import apiClient from './apiClient';
import {
  QuizAttempt,
  QuizAnswer,
  QuizResult,
  QuizGenerationRequest,
  QuizGenerationResponse,
  Question,
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  QuizHistoryFilters,
  QuizHistoryItem,
  DetailedQuizResult,
  QuizStatistics,
  QuizHistoryFilterOptions
} from '../types';

/**
 * Service for managing dynamic quiz generation, attempts, and results
 * Aligned with the new database schema and quiz attempt system
 */

/**
 * Generates a new quiz from any hierarchy level with progressive difficulty
 * @param request Quiz generation parameters
 * @returns Promise resolving to generated quiz attempt with questions
 */
export const generateQuiz = async (request: QuizGenerationRequest): Promise<QuizGenerationResponse> => {
  try {
    const response = await apiClient.post<ApiResponse<QuizGenerationResponse>>(
      '/quizzes/generate',
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to generate quiz');
    }

    if (!response.data.data) {
      throw new Error('No quiz data returned');
    }

    return response.data.data;
  } catch (error) {
    console.error('Error generating quiz:', error);
    throw error;
  }
};

/**
 * Fetches current state of a quiz attempt
 * @param attemptId The quiz attempt ID
 * @returns Promise resolving to quiz attempt with questions and answers
 */
export const getQuizAttempt = async (attemptId: string): Promise<QuizAttempt & { questions: Question[], answers: QuizAnswer[] }> => {
  try {
    const response = await apiClient.get<ApiResponse<QuizAttempt & { questions: Question[], answers: QuizAnswer[] }>>(
      `/quizzes/attempts/${attemptId}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch quiz attempt');
    }

    if (!response.data.data) {
      throw new Error('Quiz attempt not found');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error fetching quiz attempt ${attemptId}:`, error);
    throw error;
  }
};

/**
 * Submits an answer for a specific question in a quiz attempt
 * @param attemptId The quiz attempt ID
 * @param questionId The question ID
 * @param userAnswer The user's answer
 * @param questionOrder The order of the question in the quiz
 * @returns Promise resolving to updated quiz answer
 */
export const submitQuizAnswer = async (
  attemptId: string,
  questionId: number,
  userAnswer: string,
  questionOrder: number
): Promise<QuizAnswer> => {
  try {
    const response = await apiClient.put<ApiResponse<QuizAnswer>>(
      `/quizzes/attempts/${attemptId}/answer`,
      {
        questionId: questionId,
        userAnswer: userAnswer,
        questionOrder: questionOrder
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to submit answer');
    }

    if (!response.data.data) {
      throw new Error('No answer data returned');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error submitting answer for attempt ${attemptId}, question ${questionId}:`, error);
    throw error;
  }
};

/**
 * Completes a quiz attempt and calculates final scores
 * @param attemptId The quiz attempt ID
 * @returns Promise resolving to complete quiz results
 */
export const completeQuizAttempt = async (attemptId: string): Promise<QuizResult> => {
  try {
    const response = await apiClient.put<ApiResponse<QuizResult>>(
      `/quizzes/attempts/${attemptId}/complete`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to complete quiz');
    }

    if (!response.data.data) {
      throw new Error('No quiz result data returned');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error completing quiz attempt ${attemptId}:`, error);
    throw error;
  }
};

/**
 * Fetches incomplete quiz attempts for a user
 * @param userId The user ID
 * @returns Promise resolving to array of incomplete quiz attempts
 */
export const getIncompleteQuizzes = async (userId: number): Promise<QuizAttempt[]> => {
  try {
    const url = `/quizzes/attempts/user/${userId}/incomplete`;
    const response = await apiClient.get<ApiResponse<QuizAttempt[]>>(url);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch incomplete quizzes');
    }

    return response.data.data || [];
  } catch (error) {
    console.error(`Error fetching incomplete quizzes for user ${userId}:`, error);
    console.error('Error details:', error);
    throw error;
  }
};

/**
 * Resumes an incomplete quiz attempt
 * @param attemptId The quiz attempt ID
 * @returns Promise resolving to quiz attempt with current state
 */
export const resumeQuizAttempt = async (attemptId: string): Promise<QuizAttempt & { questions: Question[], answers: QuizAnswer[] }> => {
  try {
    const response = await apiClient.put<ApiResponse<QuizAttempt & { questions: Question[], answers: QuizAnswer[] }>>(
      `/quizzes/attempts/${attemptId}/resume`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to resume quiz');
    }

    if (!response.data.data) {
      throw new Error('No quiz data returned');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error resuming quiz attempt ${attemptId}:`, error);
    throw error;
  }
};

/**
 * Abandons a quiz attempt (marks it as abandoned)
 * @param attemptId The quiz attempt ID
 * @returns Promise resolving to updated quiz attempt
 */
export const abandonQuizAttempt = async (attemptId: string): Promise<QuizAttempt> => {
  try {
    const response = await apiClient.delete<ApiResponse<QuizAttempt>>(
      `/quizzes/attempts/${attemptId}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to abandon quiz');
    }

    if (!response.data.data) {
      throw new Error('No quiz data returned');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error abandoning quiz attempt ${attemptId}:`, error);
    throw error;
  }
};

/**
 * Get user's completed quiz history with filtering and pagination
 * @param userId The user ID
 * @param filters Filtering options
 * @param pagination Pagination parameters
 * @returns Promise resolving to paginated quiz history
 */
export const getCompletedQuizzes = async (
  userId: number,
  filters?: QuizHistoryFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<QuizHistoryItem>> => {
  try {
    const queryParams = new URLSearchParams();

    // Add pagination params
    if (pagination) {
      queryParams.append('page', pagination.page.toString());
      queryParams.append('limit', pagination.limit.toString());
      if (pagination.sortBy) queryParams.append('sortBy', pagination.sortBy);
      if (pagination.sortOrder) queryParams.append('sortOrder', pagination.sortOrder);
    }

    // Add filter params
    if (filters) {
      if (filters.startDate) queryParams.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) queryParams.append('endDate', filters.endDate.toISOString());
      if (filters.difficulty) queryParams.append('difficulty', filters.difficulty);
      if (filters.startFrom) queryParams.append('startFrom', filters.startFrom);
      if (filters.yearNumber) queryParams.append('yearNumber', filters.yearNumber);
      if (filters.stageId) queryParams.append('stageId', filters.stageId);
      if (filters.strandId) queryParams.append('strandId', filters.strandId);
      if (filters.substrandId) queryParams.append('substrandId', filters.substrandId);
    }

    const url = `/quizzes/attempts/user/${userId}/completed?${queryParams.toString()}`;
    const response = await apiClient.get<{
      success: boolean;
      data: QuizHistoryItem[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(url);

    if (!response.data.success) {
      throw new Error('Failed to fetch quiz history');
    }

    return {
      data: response.data.data || [],
      pagination: response.data.pagination
    };

  } catch (error) {
    console.error(`Error fetching completed quizzes for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Get detailed results for a specific quiz attempt
 * @param attemptId The quiz attempt ID
 * @returns Promise resolving to detailed quiz results
 */
export const getQuizResultDetails = async (attemptId: string): Promise<DetailedQuizResult> => {
  try {
    const response = await apiClient.get<ApiResponse<DetailedQuizResult>>(
      `/quizzes/attempts/${attemptId}/results`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch quiz results');
    }

    if (!response.data.data) {
      throw new Error('No quiz results found');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error fetching quiz results for attempt ${attemptId}:`, error);
    throw error;
  }
};

/**
 * Get quiz statistics for a user
 * @param userId The user ID
 * @returns Promise resolving to quiz statistics
 */
export const getQuizStatistics = async (userId: number): Promise<QuizStatistics> => {
  try {
    const response = await apiClient.get<ApiResponse<QuizStatistics>>(
      `/quizzes/attempts/user/${userId}/stats`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch quiz statistics');
    }

    if (!response.data.data) {
      throw new Error('No quiz statistics found');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error fetching quiz statistics for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Get available filter options for quiz history filtering
 * @returns Promise resolving to filter options
 */
export const getQuizHistoryFilterOptions = async (): Promise<QuizHistoryFilterOptions> => {
  try {
    const response = await apiClient.get<ApiResponse<QuizHistoryFilterOptions>>(
      '/quizzes/filter-options'
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch filter options');
    }

    if (!response.data.data) {
      throw new Error('No filter options data returned');
    }

    return response.data.data;
  } catch (error) {
    console.error('Error fetching quiz history filter options:', error);
    throw error;
  }
};